import React from "react";
import RuleChips from "./RuleChips";
import CheckboxGroup from "./CheckboxGroup";
import {
  findUnit, effectiveMorale, effectiveComposition, effectiveSpecialRules,
  moraleLabel, moraleClass, formatComposition, lineTotal, lineBrPerCopy, getOptionGroups, unmetOptionGroups,
} from "../lib/army";

export default function RosterRow({ faction, entry, changeQty, removeUnit, moveUnit, canMoveUp, canMoveDown, toggleGroupItem, unitAtMax, siblings }) {
  const unit = findUnit(faction, entry.unitId);
  const rowMorale = effectiveMorale(unit, entry);
  const rowComposition = effectiveComposition(unit, entry);
  const iid = entry.instanceId;

  let warning = null;
  if (unit.minQty != null && entry.qty < unit.minQty) {
    warning = <div className="row-warning">⚠ Needs at least {unit.minQty} (have {entry.qty})</div>;
  } else if (unit.maxQty != null && entry.qty > unit.maxQty) {
    warning = <div className="row-warning row-warning-danger">⚠ Exceeds max of {unit.maxQty} (have {entry.qty})</div>;
  }

  return (
    <div className="roster-row" data-testid={`roster-row-${iid}`}>
      <div className="rn-topline">
        <div className="rn-title">
          {unit.name}
          {rowMorale && <span className={"morale-tag " + moraleClass(rowMorale)}>{moraleLabel(rowMorale)}</span>}
        </div>
        <div className="rn-controls">
          <button className="remove-btn" type="button" aria-label={`Remove ${unit.name}`} onClick={() => removeUnit(iid)} data-testid={`remove-${iid}`}>×</button>
          <div className="reorder-stepper">
            <button type="button" onClick={() => moveUnit(iid, -1)} disabled={!canMoveUp} aria-label={`Move ${unit.name} up`} data-testid={`move-up-${iid}`}>▲</button>
            <button type="button" onClick={() => moveUnit(iid, +1)} disabled={!canMoveDown} aria-label={`Move ${unit.name} down`} data-testid={`move-down-${iid}`}>▼</button>
          </div>
          <div className="qty-stepper">
            <button type="button" onClick={() => changeQty(iid, -1)} aria-label="Decrease quantity" data-testid={`dec-${iid}`}>−</button>
            <span className="qty-val" data-testid={`qty-${iid}`}>{entry.qty}</span>
            <button type="button" onClick={() => changeQty(iid, +1)} aria-label="Increase quantity" disabled={unitAtMax} data-testid={`inc-${iid}`}>+</button>
          </div>
          <div className="line-points"><span className="numcell">{lineTotal(faction, entry)}</span> pts</div>
          <div className="line-br" data-testid={`line-br-${iid}`}>BR: <span className="numcell">{lineBrPerCopy(unit, entry) * entry.qty}</span></div>
        </div>
      </div>
      {unit.notes && <div className="rn-notes" data-testid={`rn-notes-${iid}`}>{unit.notes}</div>}
      <div className="rn-stats">{formatComposition(rowComposition)}</div>
      <RuleChips rules={effectiveSpecialRules(unit, entry)} />
      {warning}
      {unmetOptionGroups(unit, entry).map((g, i) => (
        <div className="row-warning" key={`req-${i}`} data-testid={`option-req-warning-${iid}-${i}`}>
          ⚠ Required selection{g.groupName ? ` for ${g.groupName}` : ""}: choose at least {g.min}
        </div>
      ))}
      <CheckboxGroup heading="Upgrades" list={unit.upgrades} entry={entry} groupKey="upgrades" onToggle={toggleGroupItem} unit={unit} siblings={siblings} />
      <CheckboxGroup heading="Special rules" list={unit.optionalSpecialRules} entry={entry} groupKey="optionalSpecialRules" onToggle={toggleGroupItem} unit={unit} siblings={siblings} />
      {renderOptions(unit, entry, toggleGroupItem, siblings)}
    </div>
  );
}

// Options render as a single "Options" group by default. When any option group
// defines a groupName, options are split per group and shown under that heading
// (groups without a groupName render with no heading); options not in any group
// fall back to the default "Options" heading.
function renderOptions(unit, entry, toggleGroupItem, siblings) {
  const options = unit.options || [];
  const groups = getOptionGroups(unit);
  const hasNamed = groups.some((g) => g.groupName);
  if (!hasNamed) {
    return <CheckboxGroup heading="Options" list={options} entry={entry} groupKey="options" onToggle={toggleGroupItem} unit={unit} siblings={siblings} />;
  }
  const grouped = new Set();
  groups.forEach((g) => g.ids.forEach((id) => grouped.add(id)));
  const ungrouped = options.filter((o) => !grouped.has(o.id));
  return (
    <>
      {groups.map((g, i) => (
        <CheckboxGroup
          key={i}
          heading={g.groupName || undefined}
          list={options.filter((o) => g.ids.includes(o.id))}
          entry={entry}
          groupKey="options"
          onToggle={toggleGroupItem}
          unit={unit}
          siblings={siblings}
        />
      ))}
      {ungrouped.length > 0 && (
        <CheckboxGroup heading="Options" list={ungrouped} entry={entry} groupKey="options" onToggle={toggleGroupItem} unit={unit} siblings={siblings} />
      )}
    </>
  );
}
